import { JSX, useEffect, useMemo, useState } from 'react';
import { useGetList, useCreatePath } from 'react-admin';
import { Link } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    Skeleton,
    Typography
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Setting } from "../../../../types/entities";
import { previewSetting, SettingPreview, SettingPreviewTone } from './setting-preview';

const MONO: string = '"Source Code Pro Regular", ui-monospace, SFMono-Regular, Menlo, monospace';

/** How many rows a section shows before it offers to expand. */
const COLLAPSED_ROWS: number = 8;

export type SettingsSection = {
    sectionTitle: string,
    path: string,
};

type Props = {
    section: SettingsSection,
    query: string,
    canEdit: boolean,
    onCount: (path: string, matches: number) => void,
};

/** Colour for a value preview, resolved against the active palette. */
function toneColor(tone: SettingPreviewTone): string {
    switch (tone) {
        case 'on':     return 'success.main';
        case 'off':    return 'text.disabled';
        case 'muted':  return 'text.disabled';
        case 'secret': return 'text.secondary';
        default:       return 'text.primary';
    }
}

/**
 * One settings family, rendered as a card that sizes to its contents.
 *
 * Each section used to be a full react-admin <List> — its own toolbar, its own
 * Create button, its own pagination and a sortable table header for a single
 * column — locked to a 600px box regardless of how few settings it held. This
 * fetches the section directly instead, so the card is only as tall as its rows.
 */
export const SettingsSectionCard: (props: Props) => JSX.Element =
    ({ section, query, canEdit, onCount }: Props): JSX.Element => {
        const [expanded, setExpanded] = useState<boolean>(false);
        const createPath = useCreatePath();

        const { data, isPending, error } = useGetList<Setting>('settings', {
            filter: { path: section.path },
            sort: { field: 'label', order: 'ASC' },
            pagination: { page: 1, perPage: 200 },
        });

        const search: string = query.trim().toLowerCase();

        const matches: Setting[] = useMemo((): Setting[] => {
            const all: Setting[] = data ?? [];
            if (search.length === 0) {
                return all;
            }
            return all.filter((setting: Setting): boolean =>
                (setting.label ?? '').toLowerCase().includes(search) ||
                (setting.code ?? '').toLowerCase().includes(search));
        }, [data, search]);

        // Let the page know how many rows survived the filter, so it can show a
        // single honest empty state instead of six empty cards.
        useEffect((): void => {
            onCount(section.path, isPending ? -1 : matches.length);
        }, [section.path, matches.length, isPending, onCount]);

        // While searching, a section with no matches gets out of the way.
        if (search.length > 0 && !isPending && matches.length === 0) {
            return null;
        }

        // Searching always shows every match; the cap is only for browsing.
        const capped: boolean = search.length === 0 && !expanded && matches.length > COLLAPSED_ROWS;
        const rows: Setting[] = capped ? matches.slice(0, COLLAPSED_ROWS) : matches;

        return (
            <Card
                variant="outlined"
                sx={{
                    breakInside: 'avoid',
                    mb: 2.5,
                    display: 'block',
                    borderRadius: 2,
                    overflow: 'hidden',
                    // Without containment the card's intrinsic width is the
                    // widest unbreakable string inside it — a 71-character
                    // setting code — and a 3-up column layout multiplies that
                    // by three, which pushed react-admin's content area out to
                    // 2098px and gave the page a horizontal scrollbar.
                    contain: 'inline-size',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            fontFamily: MONO,
                            fontSize: 10.5,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                        }}
                    >
                        { section.sectionTitle }
                    </Typography>
                    <Typography
                        sx={{
                            ml: 'auto',
                            fontFamily: MONO,
                            fontSize: 10.5,
                            color: 'text.disabled',
                        }}
                    >
                        { isPending ? '' : matches.length }
                    </Typography>
                </Box>

                { error &&
                    <Alert severity="error" sx={{ borderRadius: 0 }}>
                        { 'This section could not be loaded.' }
                    </Alert>
                }

                { isPending &&
                    <Box sx={{ px: 2, py: 1.5 }}>
                        { [0, 1, 2].map((i: number) => (
                            <Skeleton key={ 'skeleton_' + i } height={ 38 } />
                        )) }
                    </Box>
                }

                { (!isPending && !error && matches.length === 0) &&
                    <Typography sx={{ px: 2, py: 2.5, fontSize: 13, color: 'text.disabled' }}>
                        { 'No settings here yet.' }
                    </Typography>
                }

                { rows.map((setting: Setting, index: number): JSX.Element => {
                    const preview: SettingPreview = previewSetting(setting);

                    const body: JSX.Element = (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                px: 2,
                                py: 1.25,
                                borderTop: index === 0 ? 0 : 1,
                                borderColor: 'divider',
                                transition: 'background-color 120ms',
                                ...(canEdit ? { '&:hover': { bgcolor: 'action.hover' } } : {}),
                            }}
                        >
                            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                                <Typography
                                    sx={{
                                        fontSize: 13.5,
                                        fontWeight: 500,
                                        color: 'text.primary',
                                        lineHeight: 1.35,
                                    }}
                                >
                                    { setting.label }
                                </Typography>
                                <Typography
                                    noWrap
                                    sx={{
                                        fontFamily: MONO,
                                        fontSize: 10.5,
                                        color: 'text.disabled',
                                        mt: 0.25,
                                    }}
                                >
                                    { setting.code }
                                </Typography>
                            </Box>

                            <Typography
                                noWrap
                                title={ preview.tone === 'secret' ? 'Hidden' : preview.text }
                                sx={{
                                    // The value never shrinks: the label beside
                                    // it has a much larger content width, so a
                                    // shrinkable value collapsed to a single
                                    // character next to a long label. The label
                                    // wraps instead, and only a free-form value
                                    // is capped and ellipsised.
                                    flex: '0 0 auto',
                                    minWidth: 0,
                                    maxWidth: preview.fixed ? 'none' : '45%',
                                    textAlign: 'right',
                                    fontFamily: MONO,
                                    fontSize: 11.5,
                                    color: toneColor(preview.tone),
                                }}
                            >
                                { preview.text }
                            </Typography>

                            { canEdit &&
                                <ChevronRightIcon
                                    sx={{ flex: '0 0 auto', fontSize: 18, color: 'text.disabled' }} />
                            }
                        </Box>
                    );

                    // The whole row is the target, rather than a repeated EDIT
                    // link at the end of every line.
                    return canEdit
                        ? (
                            <Link
                                key={ setting.id }
                                to={ createPath({ resource: 'settings', type: 'edit', id: setting.id }) }
                                style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                            >
                                { body }
                            </Link>
                        )
                        : <Box key={ setting.id }>{ body }</Box>;
                }) }

                { capped &&
                    <Button
                        fullWidth
                        size="small"
                        onClick={ (): void => setExpanded(true) }
                        sx={{
                            borderTop: 1,
                            borderColor: 'divider',
                            borderRadius: 0,
                            py: 1,
                            fontSize: 12,
                            textTransform: 'none',
                        }}
                    >
                        { `Show all ${matches.length}` }
                    </Button>
                }
            </Card>
        );
    };
